import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  CalendarDays, MapPin, Users, Play, ExternalLink,
  Youtube, ArrowRight, Filter, Tv, Video, Mic, Calendar
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: null, label: "Tout", icon: Calendar },
  { key: "conference", label: "Conferences", icon: Mic },
  { key: "reportage", label: "Reportages", icon: Video },
  { key: "live", label: "Live", icon: Tv },
  { key: "evenement", label: "Evenements", icon: CalendarDays },
];

function extractYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch { return dateStr; }
}

function EventCard({ event }) {
  const publicUrl = event.org_slug && event.slug ? `/e/${event.org_slug}/${event.slug}` : null;
  const isPast = event.event_date && new Date(event.event_date) < new Date();

  return (
    <div className="group bg-[#151B2B] border border-[#2A3441] rounded-xl overflow-hidden hover:border-[#0099CC]/40 transition-all hover:-translate-y-1" data-testid={`event-card-${event.id}`}>
      {/* Cover */}
      <div className="relative aspect-[16/9] bg-[#0B1120] overflow-hidden">
        {event.cover_image_url ? (
          <img src={event.cover_image_url} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0099CC]/20 to-[#F59E0B]/10">
            <CalendarDays className="w-12 h-12 text-[#0099CC]/40" />
          </div>
        )}
        {/* Badge */}
        {event.media_category && (
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-black/60 backdrop-blur-sm text-white border border-white/10">
            {CATEGORIES.find(c => c.key === event.media_category)?.label || event.media_category}
          </span>
        )}
        {isPast && (
          <span className="absolute top-3 right-3 px-2 py-0.5 rounded text-[10px] font-bold bg-[#64748B]/80 text-white">Termine</span>
        )}
        {event.glotech_production && (
          <span className="absolute bottom-3 right-3 px-2 py-0.5 rounded text-[9px] font-bold bg-[#0099CC]/90 text-white">GLOTECH Production</span>
        )}
      </div>

      {/* Info */}
      <div className="p-5">
        <h3 className="text-white font-bold text-base mb-2 line-clamp-2 group-hover:text-[#0099CC] transition-colors">{event.title}</h3>
        {event.event_date && (
          <div className="flex items-center gap-1.5 text-[#94A3B8] text-xs mb-1.5">
            <CalendarDays className="w-3.5 h-3.5 text-[#F59E0B]" />
            {formatDate(event.event_date)}
          </div>
        )}
        {(event.location || event.city) && (
          <div className="flex items-center gap-1.5 text-[#94A3B8] text-xs mb-3">
            <MapPin className="w-3.5 h-3.5 text-[#0099CC]" />
            {[event.location, event.city].filter(Boolean).join(", ")}
          </div>
        )}
        {event.org_name && (
          <p className="text-[#64748B] text-xs mb-3">Organise par <span className="text-[#94A3B8] font-medium">{event.org_name}</span></p>
        )}

        {/* YouTube videos */}
        {event.youtube_urls?.length > 0 && (
          <div className="flex items-center gap-1.5 text-xs text-[#EF4444] mb-3">
            <Youtube className="w-3.5 h-3.5" /> {event.youtube_urls.length} video{event.youtube_urls.length > 1 ? "s" : ""}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t border-[#1A2332]">
          {publicUrl && (
            <Link to={publicUrl} className="flex items-center gap-1 text-xs text-[#0099CC] hover:text-white transition-colors font-medium">
              <ExternalLink className="w-3 h-3" /> Voir l'evenement
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function VideoPlayer({ url }) {
  const videoId = extractYouTubeId(url);
  if (!videoId) return null;
  return (
    <div className="aspect-video rounded-xl overflow-hidden bg-black" data-testid="youtube-player">
      <iframe
        src={`https://www.youtube.com/embed/${videoId}`}
        title="YouTube"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="w-full h-full"
      />
    </div>
  );
}

export default function EventsMediaPage() {
  const [events, setEvents] = useState([]);
  const [media, setMedia] = useState([]);
  const [category, setCategory] = useState(null);
  const [featured, setFeatured] = useState(null);
  const [socialLinks, setSocialLinks] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [evRes, mediaRes, featRes, socialRes] = await Promise.all([
          axios.get(`${API}/api/showcase/events${category ? `?category=${category}` : ""}`),
          axios.get(`${API}/api/showcase/media${category ? `?category=${category}` : ""}`),
          axios.get(`${API}/api/showcase/featured`),
          axios.get(`${API}/api/showcase/social-links`),
        ]);
        setEvents(evRes.data || []);
        setMedia(mediaRes.data || []);
        setFeatured(featRes.data);
        setSocialLinks(socialRes.data || {});
      } catch (err) {
        console.error("Showcase load error:", err);
      } finally { setLoading(false); }
    };
    load();
  }, [category]);

  // Merge events and standalone media into a combined list
  const allItems = [
    ...events,
    ...media.filter(m => !m.event_id).map(m => ({
      id: m.id,
      title: m.title,
      description: m.description,
      cover_image_url: m.poster_url,
      media_category: m.category,
      youtube_urls: m.youtube_urls || [],
      glotech_production: m.glotech_production,
      is_featured: m.is_featured,
    })),
  ];

  // Collect all YouTube URLs from featured or media for a "Videos" section
  const allVideos = [];
  media.forEach(m => { (m.youtube_urls || []).forEach(u => { if (u) allVideos.push({ url: u, title: m.title, media_id: m.id }); }); });
  events.forEach(e => { (e.youtube_urls || []).forEach(u => { if (u) allVideos.push({ url: u, title: e.title, event_id: e.id }); }); });

  const feat = featured?.event;

  return (
    <div className="min-h-screen bg-[#0B1120] text-white">
      {/* Hero Banner */}
      <section className="relative overflow-hidden" data-testid="media-hero">
        {feat?.cover_image_url ? (
          <div className="relative h-[420px]">
            <img src={feat.cover_image_url} alt={feat.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1120] via-[#0B1120]/60 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F59E0B]/20 text-[#F59E0B] text-xs font-bold mb-4 border border-[#F59E0B]/30">
                <CalendarDays className="w-3.5 h-3.5" /> A la une
              </span>
              <h1 className="text-3xl md:text-5xl font-bold mb-3 max-w-3xl">{feat.title}</h1>
              <div className="flex flex-wrap items-center gap-4 text-[#94A3B8] text-sm mb-4">
                {feat.event_date && <span className="flex items-center gap-1"><CalendarDays className="w-4 h-4 text-[#F59E0B]" />{formatDate(feat.event_date)}</span>}
                {(feat.location || feat.city) && <span className="flex items-center gap-1"><MapPin className="w-4 h-4 text-[#0099CC]" />{[feat.location, feat.city].filter(Boolean).join(", ")}</span>}
                {feat.org_name && <span>Organise par <strong className="text-white">{feat.org_name}</strong></span>}
              </div>
              {feat.org_slug && feat.slug && (
                <Link to={`/e/${feat.org_slug}/${feat.slug}`} className="inline-flex items-center gap-2 bg-[#0099CC] hover:bg-[#0088B5] text-white px-6 py-3 rounded-xl font-semibold transition-all" data-testid="featured-event-cta">
                  Decouvrir l'evenement <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          </div>
        ) : (
          <div className="relative py-20 px-8 md:px-16 bg-gradient-to-br from-[#0B1120] to-[#151B2B]">
            <h1 className="text-3xl md:text-5xl font-bold mb-4">Evenements & Medias</h1>
            <p className="text-[#94A3B8] text-lg max-w-2xl">
              Vos evenements meritent d'etre vus. Conferences, rencontres, reportages et diffusion audiovisuelle : decouvrez les evenements accompagnes par GLOTECH DIGITAL.
            </p>
          </div>
        )}
      </section>

      {/* Category Filters */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center gap-2 flex-wrap" data-testid="category-filters">
          {CATEGORIES.map(cat => (
            <button
              key={cat.key || "all"}
              onClick={() => setCategory(cat.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                category === cat.key
                  ? "bg-[#0099CC] text-white"
                  : "bg-[#151B2B] text-[#94A3B8] hover:bg-[#1A2332] border border-[#2A3441]"
              }`}
              data-testid={`filter-${cat.key || "all"}`}
            >
              <cat.icon className="w-3.5 h-3.5" /> {cat.label}
            </button>
          ))}
        </div>
      </section>

      {/* Events Grid */}
      <section className="max-w-7xl mx-auto px-6 pb-12">
        {loading ? (
          <div className="text-center py-20 text-[#64748B]">Chargement...</div>
        ) : allItems.length === 0 ? (
          <div className="text-center py-20">
            <CalendarDays className="w-12 h-12 text-[#2A3441] mx-auto mb-4" />
            <p className="text-[#64748B]">Aucun evenement pour le moment.</p>
            <p className="text-[#4A5568] text-sm mt-1">Les evenements publies apparaitront ici automatiquement.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="events-grid">
            {allItems.map(item => <EventCard key={item.id} event={item} />)}
          </div>
        )}
      </section>

      {/* Videos Section */}
      {allVideos.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 pb-16" data-testid="videos-section">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Youtube className="w-6 h-6 text-[#EF4444]" /> Videos et temps forts
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {allVideos.slice(0, 6).map((v, i) => {
              const vid = extractYouTubeId(v.url);
              return (
                <div
                  key={i}
                  className="group relative aspect-video rounded-xl overflow-hidden bg-[#151B2B] border border-[#2A3441] cursor-pointer hover:border-[#EF4444]/40 transition-all"
                  onClick={() => setSelectedVideo(v.url)}
                  data-testid={`video-thumb-${i}`}
                >
                  {vid && <img src={`https://img.youtube.com/vi/${vid}/hqdefault.jpg`} alt={v.title} className="w-full h-full object-cover" />}
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/20 transition-all">
                    <div className="w-14 h-14 rounded-full bg-[#EF4444] flex items-center justify-center shadow-lg shadow-[#EF4444]/30">
                      <Play className="w-6 h-6 text-white ml-0.5" />
                    </div>
                  </div>
                  <p className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent text-xs text-white font-medium">{v.title}</p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Video Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6" onClick={() => setSelectedVideo(null)} data-testid="video-modal">
          <div className="w-full max-w-4xl" onClick={e => e.stopPropagation()}>
            <VideoPlayer url={selectedVideo} />
            <button onClick={() => setSelectedVideo(null)} className="mt-4 text-[#94A3B8] hover:text-white text-sm mx-auto block">Fermer</button>
          </div>
        </div>
      )}

      {/* Social Links */}
      <section className="max-w-7xl mx-auto px-6 pb-16" data-testid="social-section">
        <h2 className="text-2xl font-bold mb-6">Suivez GLOTECH DIGITAL</h2>
        <div className="flex flex-wrap gap-3">
          {socialLinks.youtube && (
            <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-5 py-3 rounded-xl bg-[#EF4444]/10 border border-[#EF4444]/20 text-[#EF4444] hover:bg-[#EF4444]/20 transition-all font-medium" data-testid="social-youtube">
              <Youtube className="w-5 h-5" /> YouTube <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
          {socialLinks.tiktok && (
            <a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-5 py-3 rounded-xl bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-all font-medium" data-testid="social-tiktok">
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.11V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.75a8.18 8.18 0 004.76 1.52V6.84a4.84 4.84 0 01-1-.15z"/></svg>
              TikTok <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
          {socialLinks.facebook && (
            <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-5 py-3 rounded-xl bg-[#1877F2]/10 border border-[#1877F2]/20 text-[#1877F2] hover:bg-[#1877F2]/20 transition-all font-medium" data-testid="social-facebook">
              Facebook <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
          {socialLinks.instagram && (
            <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-5 py-3 rounded-xl bg-[#E4405F]/10 border border-[#E4405F]/20 text-[#E4405F] hover:bg-[#E4405F]/20 transition-all font-medium" data-testid="social-instagram">
              Instagram <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </section>
    </div>
  );
}
